// MACHINERY V2 (Stroje) — the phone nav drawer (M2.16a).
//
// The hamburger's target. Below 768 the 52px ChromeNavV4 is replaced by a 48px
// header (MobilePhoneHeader.tsx), which has room for a logo, an avatar and a
// hamburger and nothing else — so everything else that bar carried lives here.
//
// ============================================================
//  PURE GLOBAL NAV. NO PAGE ACTIONS. EVER.
// ============================================================
// It is mounted by MachineryV2Layout, which wraps the WHOLE v2 subtree — fleet,
// katalog, profile, SR, add — so a page-specific action placed here would
// follow the user onto four pages it does not apply to. That is also why the
// fleet's "Spravovat skupiny" sits in a kebab on the fleet's own header instead
// (FleetMobileHeader.tsx). This list must survive M2.12's promotion of the nav
// to app-wide with nothing to unpick.
//
// GLOBAL CHROME, HOWEVER, IS EXACTLY WHAT BELONGS HERE — which is why the
// palette switch does. It is not a page action: it drives the root class on
// every v2 page (MachineryV2Layout).
//
// ============================================================
//  NOTHING HERE IS A SECOND COPY OF ANYTHING
// ============================================================
// Everything below is the desktop chrome's own implementation, imported:
//   · module tabs   — NAV_MODULES, gated through `canSeeModule` AND
//     `useTypeAllows`, both from navModules.ts. The company-type gate was
//     forked into this file verbatim, escape hatch and all, in the same commit
//     that hoisted logout and language into a shared hook; two forks of a
//     VISIBILITY gate is how a permission bug is born at M2.12.
//   · Midas         — BRAND accent var(--accent), never a module accent: it is
//     global, reachable from every module (ChromeNavV4's rule, verbatim).
//   · notifications — re-homed, not added. The bell is in the right cluster the
//     phone header replaces, and the mobile pass never removes a working
//     control. Its unread read is ChromeNavV4's, unchanged and still READ-ONLY:
//     the legacy bell owns fetchUnreadCount and the SignalR subscription, and
//     M2.12's right-cluster audit must re-home those before the legacy bar
//     retires or this badge silently reads 0 forever.
//   · identity      — useChromeIdentity, the derivation the desktop user menu
//     renders, with its open M2.12 obligation attached.
//   · language      — useChromeLanguage plus the SHARED, strongly-typed code
//     and name tables.
//   · palette       — RE-HOMED, not invented. Until this pass,
//     ChromeRightMenus.tsx:78 was the ONLY `setPalette` dispatcher in the
//     entire application (`grep -rn setPalette src/` — that grep is the
//     standard of proof), and below 768 that menu never mounts, so a phone user
//     was locked into whatever a desktop session last chose while `palette`
//     kept driving the root class. The chip below is the second dispatcher and
//     is meant to be; the OPTION TABLE is shared, and the standing "interim,
//     re-decide at M2.12" note lives with the desktop section.
//   · log-out       — useChromeLogout, so a session teardown cannot drift.
// The SECTIONS and the CHIPS are the sheet kit's (mobile/sheetAtoms.tsx), not
// drawer-private re-drawings of them.
//
// ============================================================
//  IT IS A BOTTOM SHEET, NOT A SIDE PANEL
// ============================================================
// No board draws this drawer — the package only draws the hamburger. Rather
// than invent a second sheet chrome (a different radius, a different entrance,
// a second set of dismissal behaviours to get right), it reuses SheetV2: one
// gesture language for every mobile overlay in the module, and the portal rule,
// the propagation guards and the reduced-motion story are already solved there.
// The language and palette rows are CHIPS for the same reason they are in the
// fleet sheet: three or two options fit, and a menu opened inside a sheet would
// need the 1400 z-index tier to clear the sheet it is borne by.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { useNavigate } from "react-router-dom";

import { Modules } from "../../../../core/models/PermissionModels";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { setPalette } from "../machineryV2UiSlice";
import SheetV2 from "../mobile/SheetV2";
import { SheetChip, SheetChipRow, SheetSection } from "../mobile/sheetAtoms";
import { NgBell, NgExit, NgGear } from "./ChromeNavIcons";
import { ChromeNavModuleKey, NAV_MODULES, canSeeModule, useTypeAllows } from "./navModules";
import { DrawerIdentity, DrawerRow } from "./mobileDrawerRows";
import {
  LANG_CODES,
  LANG_NAMES,
  PALETTE_OPTIONS,
  useChromeIdentity,
  useChromeLanguage,
  useChromeLogout,
} from "./useChromeActions";

export interface MobileDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which module owns the current page — same prop, same default, as
   *  ChromeNavV4's. */
  active?: ChromeNavModuleKey;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ open, onOpenChange, active = "stroje" }) => {
  const { translate } = useTranslation(["layout", "common", "myMachinery"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const typeAllows = useTypeAllows();
  const isBotEnabled = useAppSelector((s) => s.chatBot.isBotEnabled);
  const unreadCount = useAppSelector((s: any) => s.notifications?.unreadCount ?? 0);
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  const { user, initial, subLine } = useChromeIdentity();
  const { current, languages, select } = useChromeLanguage();
  const logout = useChromeLogout();

  const activeAccent =
    NAV_MODULES.find((m) => m.key === active)?.accent || "var(--mod-accent)";

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <SheetV2
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:v2.mobile.menu")}
      footer={
        <button
          type="button"
          className="btn"
          onClick={() => onOpenChange(false)}
          style={{
            flex: 1,
            height: 46,
            minWidth: 44,
            justifyContent: "center",
            borderRadius: 10,
            fontSize: 13.5,
          }}
        >
          {translate("myMachinery:close")}
        </button>
      }
    >
      {user && (
        <DrawerIdentity
          initial={initial}
          name={user.firstName}
          subLine={subLine}
          accent={activeAccent}
        />
      )}

      <SheetSection title={translate("myMachinery:v2.mobile.navSection")}>
        {NAV_MODULES.filter(
          (m) => canSeeModule(m.module) && typeAllows(m.operatorOnly)
        ).map((m) => (
          <DrawerRow
            key={m.key}
            label={translate(m.labelKey)}
            icon={<m.Icon />}
            accent={m.key === active ? m.accent : undefined}
            active={m.key === active}
            onSelect={() => go(m.path)}
          />
        ))}
        {isBotEnabled && canSeeModule(Modules.Midas) && (
          <DrawerRow
            label={translate("layout:chromeV4.midas")}
            accent="var(--accent)"
            icon={
              <span
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: "var(--accent)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                M
              </span>
            }
            onSelect={() => go("/midas")}
          />
        )}
        {/* /notifications sits behind PermissionRouteGuard(Notifications):
            without this gate a Notifications:None user gets a row that silently
            redirects. Same gate ChromeNavV4 puts on the bell, and the same one
            MobilePhoneHeader puts on the hamburger's count. */}
        {canSeeModule(Modules.Notifications) && (
          <DrawerRow
            label={translate("layout:chromeV4.notifications")}
            icon={<NgBell />}
            badge={unreadCount}
            onSelect={() => go("/notifications")}
          />
        )}
      </SheetSection>

      <SheetSection title={translate("layout:chromeV4.language")}>
        <SheetChipRow>
          {languages.map((lng) => (
            <SheetChip
              key={lng}
              variant="code"
              label={LANG_CODES[lng]}
              ariaLabel={LANG_NAMES[lng]}
              active={lng === current}
              onClick={() => select(lng)}
            />
          ))}
        </SheetChipRow>
      </SheetSection>

      <SheetSection title={translate("myMachinery:v2.mobile.accountSection")}>
        {/* Palette first, then Settings, then log-out — the desktop user menu's
            own order (ChromeRightMenus.tsx's MenuContent), so a user who knows
            one surface finds the same thing in the same place on the other. */}
        <SheetSection title={translate("myMachinery:v2.palette.label")} divider={false}>
          <SheetChipRow>
            {PALETTE_OPTIONS.map((p) => (
              <SheetChip
                key={p.id}
                label={translate(p.labelKey)}
                active={palette === p.id}
                onClick={() => dispatch(setPalette(p.id))}
              />
            ))}
          </SheetChipRow>
        </SheetSection>
        {/* Mirrors the desktop user menu exactly: /settings is gated because it
            sits behind PermissionRouteGuard(Settings), log-out needs no gate. */}
        {canSeeModule(Modules.Settings) && (
          <DrawerRow
            label={translate("common:navigation.settings")}
            icon={<NgGear />}
            onSelect={() => go("/settings")}
          />
        )}
        <DrawerRow
          label={translate("common:navigation.logout")}
          icon={<NgExit />}
          onSelect={() => {
            onOpenChange(false);
            logout();
          }}
        />
      </SheetSection>
    </SheetV2>
  );
};

export default MobileDrawer;
